import React, { useMemo } from 'react';
import { ListCheckIcon } from '@revfluence/fresh-icons/regular/esm';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { GetAllStagesQuery_getAllStages } from '@frontend/app/queries/types/GetAllStagesQuery';
import { TWorklet } from '../../types';
import { StageToCreatorInteractionMap, TaskItem } from './TaskItem';
import { useCampaign } from '../CampaignContext';

interface Props {
  stages?: TWorklet[];
  isLoading?: boolean;
  customStages?: GetAllStagesQuery_getAllStages[];
}

export const TaskList: React.FC<Props> = ({ stages, isLoading, customStages }) => {
  const { initialTasks, remainingTasks } = useCampaign();

  const orderedTasks = useMemo(() => {
    const allTasks = [...(initialTasks || []), ...(remainingTasks || [])];
    const filteredStages = stages?.filter((worklet) => {
      if (worklet?.specURI?.startsWith('CustomWorklet-')) {
        const customStage = customStages?.find((stage) => stage.name === worklet.specTitle);
        return customStage?.tasks[0]?.creatorAccess;
      }
      return !!StageToCreatorInteractionMap[worklet?.specURI];
    }) ?? [];
    return filteredStages.map((worklet) => {
      const taskConfig = allTasks?.find((task) => task.specURI === worklet.specURI);
      if (taskConfig) {
        return {
          id: taskConfig.id,
          specURI: taskConfig.specURI,
          isInitial: taskConfig.isInitial,
          order: taskConfig.order,
          specTitle: worklet.specTitle,
        };
      }
      return {
        specURI: worklet.specURI,
        isInitial: false,
        order: 999, // Hardcoded to be last in the list
        specTitle: worklet.specTitle,
      };
    }).sort((a, b) => a.order - b.order);
  }, [initialTasks, remainingTasks, stages, customStages]);

  // If the first task is initial, then the following tasks are blocked
  const isBlocked = orderedTasks?.[0]?.isInitial;

  return (
    <div className="rounded-2xl bg-white my-4 mx-6 p-3 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <ListCheckIcon className="h-4 w-4 text-grey-6" />
        <h3 className="mb-0 text-sm font-medium text-grey-6">Tasks</h3>
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {orderedTasks?.map((task) => (
            <TaskItem key={task.specURI} task={task} isBlocked={isBlocked} />
          ))}
          {!orderedTasks?.length && (
            <p className="text-center text-sm text-grey-4">
              No actionable tasks for creator
            </p>
          )}
        </div>
      )}
    </div>
  );
};
